import { Card, Descriptions, Button, Space, message, Modal, Tag } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getPanel, deletePanel } from '@/api/panels';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
// 导入配套图标
import { ArrowLeftOutlined, UpOutlined, DeleteOutlined, WarningOutlined, AppleOutlined, AndroidOutlined, InfoCircleOutlined } from '@ant-design/icons';
/**
 * 面板详情页面
 */
export const PanelDetail = () => {
  const { panelId } = useParams<{ panelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 查询面板详情
  const panelQuery = useQuery({
    queryKey: ['panel', developerId, panelId],
    queryFn: () => {
      if (!panelId) {
        throw new Error('面板 ID 不能为空');
      }
      return getPanel(panelId);
    },
    enabled: !!panelId && !!developerId,
  });

  const panel = panelQuery.data;
  const loading = panelQuery.isLoading;
  const error = panelQuery.error;

  /**
   * 处理升级面板
   */
  const handleUpdate = (): void => {
    if (panelId) {
      navigate(`/panels/${panelId}/update`);
    }
  };

  /**
   * 处理删除面板
   */
  const handleDelete = (): void => {
    if (!panelId || !panel) return;

    Modal.confirm({
      title: '确认删除面板',
      icon: <WarningOutlined className="text-orange-500" />,
      content: `确定要删除面板 "${panel.name}" 吗？此操作不可恢复，已关联该面板的产品将无法正常展示控制界面！`,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePanel(panelId);
          message.success('面板删除成功');
          await queryClient.invalidateQueries({ queryKey: ['panels', developerId] });
          navigate('/panels');
        } catch {
          message.error('请先确保没有关联到产品，再进行删除操作。');
        }
      },
    });
  };

  /**
   * 处理返回列表
   */
  const handleBack = (): void => {
    navigate('/panels');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载面板详情，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  if (!panel) {
    return (
      <ErrorAlert 
        message="未找到面板" 
        description="指定的面板不存在或已被删除"
        className="mb-8"
      />
    );
  }

  // 平台类型映射
  const platformMap: Record<string, { name: string; color: string; icon: React.ReactNode }> = {
    jsbundle: { name: 'iOS', color: 'blue', icon: <AppleOutlined className="text-xs mr-1" /> },
    bundle: { name: 'Android', color: 'green', icon: <AndroidOutlined className="text-xs mr-1" /> },
  };
  const currentPlatform = platformMap[panel.file_type] || { name: panel.file_type, color: 'gray', icon: null };

  // 文件大小格式化（处理 undefined/null）
  const formatFileSize = (size: number | null | undefined): string => {
    if (size == null) return '—'; // 仅返回纯字符，移除span元素
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="w-full space-y-8">
      {/* 标题+操作区 - 适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-500" />
            面板详情
          </h1>
          <p className="text-gray-500 mt-1">查看 {panel.name} 的详细配置信息，支持升级、图片管理和删除操作。</p>
        </div>
        <Space size="middle" wrap>
          <Button 
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回列表
          </Button>
          <Button 
            type="primary" 
            onClick={handleUpdate}
            icon={<UpOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            升级面板
          </Button>
          {/* <Button 
            icon={<PictureOutlined />} 
            onClick={() => navigate(`/panels/${panelId}/images`)}
            className="px-6 hover:shadow-md transition-shadow"
          >
            图片管理
          </Button> */}
          <Button 
            danger 
            onClick={handleDelete}
            icon={<DeleteOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            删除面板
          </Button>
        </Space>
      </div>

      {/* 基本信息卡片 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 max-w-3xl"
        style={{ 
          padding: '24px',
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': 600,
          '--ant-card-head-color': '#1D2129'
        } as React.CSSProperties}
      >
        <Descriptions
          column={1}
          bordered={false}
          labelStyle={{ width: '160px', fontWeight: 600, color: '#667085' }}
          contentStyle={{ color: '#1D2129' }}
          style={{
            '--ant-descriptions-item-margin-bottom': '16px',
            fontSize: '15px'
          } as React.CSSProperties}
        >
          <Descriptions.Item label="面板名称">{panel.name}</Descriptions.Item>
          <Descriptions.Item label="面板ID">
            <span className="font-mono">{panel.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="适配平台">
            <Tag color={currentPlatform.color}>{currentPlatform.icon}{currentPlatform.name}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="面板版本">
            <span className="text-purple-600 font-medium">v{panel.version}</span>
          </Descriptions.Item>
          <Descriptions.Item label="文件大小">{formatFileSize(panel.file_size)}</Descriptions.Item>
          <Descriptions.Item label="文件类型">
            <span className="font-mono">{panel.file_type}</span>
          </Descriptions.Item>
          {/* <Descriptions.Item label="文件校验和">
            {panel.file_checksum 
              ? <span className="font-mono text-gray-700">{panel.file_checksum.substring(0, 10)}...</span>
              : <span className="text-gray-400">—</span>
            }
          </Descriptions.Item> */}
          <Descriptions.Item label="创建时间">
            {new Date(panel.created_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};